import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { usePathname } from 'expo-router';
import {
  getPetById,
  loadPetHoverImage,
  loadPetImage,
  loadPetStreamingImages,
} from '@/constants/pets';
import { accountApi } from '@/lib/api';
import { subscribePetReplyCompleted } from '@/lib/socket';
import { isArtifactSidebarType } from '@/constants/utils/files';
import {
  useAuthStore,
  useChatStore,
  useChatInputStore,
  useModalStore,
  useRouteStore,
  useUIStore,
  useVoiceModeStore,
} from '@/store';
import { useArtifactViewerStore } from '@/store/artifactViewer';
import useResponsiveness from '@/hooks/useResponsiveness';
import Tooltip from '@/components/Tooltip';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';

const SPEECH_DURATION = 12000;
const getSpeechReplyTarget = () => 2 + Math.floor(Math.random() * 2);

const styles = StyleSheet.create({
  host: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    right: 18,
    bottom: 16,
    zIndex: 900,
    pointerEvents: 'box-none',
  },
  button: {
    width: 104,
    height: 104,
    alignItems: 'center',
    justifyContent: 'flex-end',
    borderRadius: 24,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'transform 160ms ease, filter 160ms ease',
      },
      default: {},
    }),
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.96 }],
  },
  image: {
    width: 100,
    height: 100,
  },
  shadow: {
    position: 'absolute',
    bottom: 1,
    width: 56,
    height: 9,
    borderRadius: 30,
    backgroundColor: 'rgba(0, 0, 0, 0.14)',
    transform: [{ scaleY: 0.45 }],
  },
  speechBubble: {
    position: 'absolute',
    right: 74,
    bottom: 88,
    width: 176,
    paddingHorizontal: 13,
    paddingVertical: 9,
    borderWidth: 1,
    borderRadius: 14,
    ...Platform.select({
      web: {
        boxShadow: '0 5px 18px rgba(0, 0, 0, 0.14)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.14,
        shadowRadius: 9,
      },
    }),
  },
  speechText: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  speechTail: {
    position: 'absolute',
    right: 16,
    bottom: -6,
    width: 11,
    height: 11,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    transform: [{ rotate: '45deg' }],
  },
});

export default function DesktopPetHost() {
  const { colors } = useTheme();
  const { isMD } = useResponsiveness();
  const user = useAuthStore(state => state.user);
  const selectedPetId = user?.preferences?.appearance?.selectedPet || null;
  const isModalOpen = useModalStore(state => state.isOpen);
  const { openModal } = useModalStore(state => state.actions);
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isArtifactViewerOpen = useArtifactViewerStore(state => state.isOpen);
  const isArtifactOpen = isArtifactViewerOpen
    || (isContextSidebarVisible && isArtifactSidebarType(contextSidebarType));
  const pathname = usePathname();
  // Pets only live inside chat screens: conversations (/c), agent chats (/g),
  // and model chats (/m). Everywhere else (home, explore, settings, ...) they
  // should stay hidden.
  const isChatRoute = /^\/(c|g|m)\//.test(pathname || '');
  const routeStoreConversationId = useRouteStore(state => state.currentLibraryId);
  const pathnameConversationId = pathname?.match(/^\/c\/([^/]+)/)?.[1] || null;
  const currentConversationId = pathnameConversationId || routeStoreConversationId;
  const hasComposerDraft = useChatInputStore(state => {
    const draft = state.inputStates?.[state.currentContextId];
    return Boolean(draft?.text?.trim() || draft?.files?.length);
  });
  const isInferenceActive = useChatStore(state => {
    const activeChat = (state.chats || []).find(chat => chat.id === currentConversationId);
    return Boolean(
      activeChat?.isInferencing ||
      activeChat?.isInferencingWaiting ||
      (activeChat?.messages || []).some(message =>
        message?.role === 'assistant' &&
        (message.isStreaming || message.isTyping || message.isWaiting || message.isThinking)
      )
    );
  });
  const pet = useMemo(() => getPetById(selectedPetId), [selectedPetId]);
  const bob = useRef(new Animated.Value(0)).current;
  const hover = useRef(new Animated.Value(0)).current;
  const speechCadenceByConversation = useRef(new Map());
  const speechRequestVersion = useRef(0);
  const isSpeechRequestPending = useRef(false);
  const speechHideTimer = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [streamingFrameIndex, setStreamingFrameIndex] = useState(-1);
  const [isHovered, setIsHovered] = useState(false);
  const [petImages, setPetImages] = useState({
    default: null,
    hover: null,
    streaming: [],
  });
  const [speechBubble, setSpeechBubble] = useState(null);
  const isDesktop = Platform.OS === 'web' || Platform.OS === 'macos';
  const petImage = petImages.default;
  const hoverImage = petImages.hover;
  const streamingImages = petImages.streaming;

  useEffect(() => {
    let cancelled = false;
    setPetImages({ default: null, hover: null, streaming: [] });

    if (!pet) return undefined;

    loadPetImage(pet)
      .then(image => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, default: image }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s image:`, error));

    return () => {
      cancelled = true;
    };
  }, [pet]);

  useEffect(() => {
    let cancelled = false;
    if (!pet || !isHovered || hoverImage) return undefined;

    loadPetHoverImage(pet)
      .then(image => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, hover: image }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s hover image:`, error));

    return () => {
      cancelled = true;
    };
  }, [hoverImage, isHovered, pet]);

  useEffect(() => {
    let cancelled = false;
    if (!pet || !isInferenceActive || streamingImages.length > 0) return undefined;

    loadPetStreamingImages(pet)
      .then(images => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, streaming: images }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s animation images:`, error));

    return () => {
      cancelled = true;
    };
  }, [isInferenceActive, pet, streamingImages.length]);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then(value => {
      if (mounted) setReduceMotion(value);
    });
    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  useEffect(() => {
    bob.stopAnimation();
    bob.setValue(0);
    if (!pet || reduceMotion) return undefined;

    const duration = isInferenceActive ? 360 : 1500;
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(bob, {
          toValue: 1,
          duration,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(bob, {
          toValue: 0,
          duration,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [bob, isInferenceActive, pet, reduceMotion]);

  useEffect(() => {
    hover.stopAnimation();
    if (!isHovered || reduceMotion) {
      Animated.spring(hover, {
        toValue: 0,
        damping: 10,
        stiffness: 220,
        useNativeDriver: true,
      }).start();
      return undefined;
    }

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(hover, {
          toValue: 1,
          duration: 240,
          easing: Easing.out(Easing.back(1.4)),
          useNativeDriver: true,
        }),
        Animated.delay(280),
        Animated.timing(hover, {
          toValue: 0,
          duration: 360,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.delay(500),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [hover, isHovered, reduceMotion]);

  useEffect(() => {
    const frames = streamingImages;
    if (!isInferenceActive || frames.length === 0) {
      setStreamingFrameIndex(-1);
      return undefined;
    }

    const firstFrame = Math.floor(Math.random() * frames.length);
    setStreamingFrameIndex(firstFrame);

    if (reduceMotion || frames.length < 2) {
      return undefined;
    }

    const interval = setInterval(() => {
      setStreamingFrameIndex(current => (current + 1) % frames.length);
    }, 1100);
    return () => clearInterval(interval);
  }, [isInferenceActive, reduceMotion, streamingImages]);

  useEffect(() => {
    speechRequestVersion.current += 1;
    isSpeechRequestPending.current = false;
    clearTimeout(speechHideTimer.current);
    setSpeechBubble(null);
  }, [currentConversationId, selectedPetId]);

  useEffect(() => {
    return subscribePetReplyCompleted(completedConversationId => {
      if (
        completedConversationId !== currentConversationId ||
        !isDesktop ||
        isMD ||
        !pet ||
        isVoiceModeActive ||
        isModalOpen ||
        isArtifactOpen ||
        isSpeechRequestPending.current
      ) {
        return;
      }

      const cadence = speechCadenceByConversation.current.get(completedConversationId) || {
        completedReplies: 0,
        // Let the user discover the feature on the first completed reply.
        // Subsequent reactions stay occasional so the pet is not distracting.
        target: 1,
      };
      cadence.completedReplies += 1;
      speechCadenceByConversation.current.set(completedConversationId, cadence);
      if (cadence.completedReplies < cadence.target) return;

      cadence.completedReplies = 0;
      cadence.target = getSpeechReplyTarget();
      const requestVersion = ++speechRequestVersion.current;
      isSpeechRequestPending.current = true;

      accountApi.generatePetSpeech(completedConversationId)
        .then(speech => {
          if (requestVersion !== speechRequestVersion.current || !speech) return;
          setSpeechBubble(speech);
          clearTimeout(speechHideTimer.current);
          speechHideTimer.current = setTimeout(() => {
            if (requestVersion === speechRequestVersion.current) {
              setSpeechBubble(null);
            }
          }, SPEECH_DURATION);
        })
        .catch(error => {
          console.error('Unable to generate chat companion speech:', error);
        })
        .finally(() => {
          if (requestVersion === speechRequestVersion.current) {
            isSpeechRequestPending.current = false;
          }
        });
    });
  }, [
    currentConversationId,
    isDesktop,
    isArtifactOpen,
    isModalOpen,
    isVoiceModeActive,
    isMD,
    pet,
  ]);

  useEffect(() => () => {
    speechRequestVersion.current += 1;
    clearTimeout(speechHideTimer.current);
  }, []);

  // The companion picker is opt-in only (pet click, Settings → Appearance,
  // home). It used to auto-open right after new-user onboarding, which landed
  // as an unexpected second popup; that first-run trigger was removed.

  const handlePress = useCallback(() => {
    openModal('PetPickerModal');
  }, [openModal]);

  if (!isChatRoute || !isDesktop || isMD || !pet || !petImage || isVoiceModeActive || isModalOpen || isArtifactOpen) {
    return null;
  }

  const animatedStyle = reduceMotion ? null : {
    transform: [
      {
        translateY: bob.interpolate({
          inputRange: [0, 1],
          outputRange: [0, isInferenceActive ? -10 : -6],
        }),
      },
      {
        scale: bob.interpolate({
          inputRange: [0, 1],
          outputRange: [1, isInferenceActive ? 1.06 : 1.01],
        }),
      },
      {
        rotate: bob.interpolate({
          inputRange: [0, 1],
          outputRange: ['0deg', isInferenceActive ? '-2deg' : '0deg'],
        }),
      },
    ],
  };

  const hoverStyle = reduceMotion ? null : {
    transform: [
      {
        translateY: hover.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -8],
        }),
      },
      {
        rotate: hover.interpolate({
          inputRange: [0, 1],
          outputRange: ['0deg', '3deg'],
        }),
      },
      {
        scale: hover.interpolate({
          inputRange: [0, 1],
          outputRange: [1, 1.1],
        }),
      },
    ],
  };

  const streamingImage = isInferenceActive && streamingFrameIndex >= 0
    ? streamingImages[streamingFrameIndex]
    : null;
  const displayedImage = isHovered && hoverImage
    ? hoverImage
    : streamingImage || petImage;

  const activityLabel = isInferenceActive
    ? `${pet.name} is helping with your answer`
    : `${pet.name}, your chat companion. Press to change companion.`;

  return (
    <View
      // The floating companion can overlap Send/Stop. Let those controls
      // receive clicks while composing or working, including on macOS where
      // the transparent portion of the companion is still a native hit target.
      pointerEvents={isInferenceActive || hasComposerDraft ? 'none' : 'box-none'}
      accessibilityElementsHidden={isInferenceActive || hasComposerDraft}
      importantForAccessibility={isInferenceActive || hasComposerDraft ? 'no-hide-descendants' : 'auto'}
      style={styles.host}
    >
      {speechBubble ? (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={[
            styles.speechBubble,
            {
              backgroundColor: colors.solid_color_block,
              borderColor: colors.border_color_secondary,
            },
          ]}
        >
          <Text color="primary" style={styles.speechText}>{speechBubble}</Text>
          <View
            style={[
              styles.speechTail,
              {
                backgroundColor: colors.solid_color_block,
                borderColor: colors.border_color_secondary,
              },
            ]}
          />
        </View>
      ) : null}
      <Tooltip label={isInferenceActive ? `${pet.name} is working…` : `${pet.name} · Change companion`}>
        <Animated.View style={animatedStyle}>
          <Animated.View style={hoverStyle}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={activityLabel}
              accessibilityState={{ busy: isInferenceActive }}
              onHoverIn={() => setIsHovered(true)}
              onHoverOut={() => setIsHovered(false)}
              onPress={handlePress}
              style={({ pressed }) => [styles.button, pressed && styles.pressed]}
            >
              <View style={styles.shadow} />
              <Image
                source={displayedImage}
                style={styles.image}
                contentFit="contain"
                transition={reduceMotion ? 0 : 100}
              />
            </Pressable>
          </Animated.View>
        </Animated.View>
      </Tooltip>
    </View>
  );
}
